import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, message } from 'antd';
import { updateCurrentPwd } from '../pages/Login/LoginPresenter';
import { isForceUpdatePasswordRequired, setForceUpdatePassword } from '../utils/forcePasswordUpdate';
import { registerPasswordUpdateHandler } from '../utils/passwordUpdateBus';
import { getItem, setItem } from '../utils/localStorageUtil';
import { LOCAL_STORAGE_KEYS } from '../const/localStorageConst';

const PasswordUpdateModalContext = createContext({
  openPasswordUpdateModal: () => {},
});

/**
 * 获取弹窗是否应该按“强制修改密码”模式展示。
 * 只要 localStorage 中 force_update_password 仍为 0，就不能手动关闭。
 */
const getModalForcedState = (options = {}) => {
  return Boolean(options.forced) || isForceUpdatePasswordRequired();
};

/**
 * 全局修改密码弹窗 Provider
 *
 * 负责两类场景：
 * 1. 强制改密：登录后/刷新后/API请求被拦截时，force_update_password === '0'，弹窗不可关闭。
 * 2. 手动改密：普通用户点击右上角“修改密码”按钮，弹窗可关闭。
 */
export const PasswordUpdateModalProvider = ({ children }) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [forced, setForced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshAfterSuccess, setRefreshAfterSuccess] = useState(false);

  const openPasswordUpdateModal = useCallback((options = {}) => {
    const nextForced = getModalForcedState(options);
    setForced((current) => current || nextForced);
    setRefreshAfterSuccess(Boolean(options.refreshAfterSuccess) || nextForced);
    setOpen(true);
  }, []);

  const closePasswordUpdateModal = useCallback(() => {
    // 强制模式下不允许手动关闭
    if (forced || isForceUpdatePasswordRequired()) {
      return;
    }
    setOpen(false);
    setForced(false);
    setRefreshAfterSuccess(false);
    form.resetFields();
  }, [forced, form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const res = await updateCurrentPwd(values.newPassword);

      if (res?.status === 200) {
        setForceUpdatePassword('1');
        // 修改密码成功后同步刷新“记住密码”的本地密码值，避免下次自动回填旧密码
        setItem(LOCAL_STORAGE_KEYS.LOGIN_PWD, values.newPassword);
        message.success('密码修改成功');

        if (refreshAfterSuccess) {
          // 强制改密成功后刷新整个页面，重新触发页面初始化接口请求
          window.location.reload();
          return;
        }

        setOpen(false);
        setForced(false);
        setRefreshAfterSuccess(false);
        form.resetFields();
      } else {
        message.error(res?.msg || '密码修改失败');
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error('密码修改失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  }, [form, refreshAfterSuccess]);

  useEffect(() => {
    const unregister = registerPasswordUpdateHandler((options = {}) => {
      openPasswordUpdateModal(options);
    });
    return unregister;
  }, [openPasswordUpdateModal]);

  useEffect(() => {
    if (isForceUpdatePasswordRequired() && getItem(LOCAL_STORAGE_KEYS.TOKEN)) {
      openPasswordUpdateModal({ forced: true, source: 'startup', refreshAfterSuccess: true });
    }
  }, [openPasswordUpdateModal]);

  useEffect(() => {
    const onSessionCleared = () => {
      setOpen(false);
      setForced(false);
      setRefreshAfterSuccess(false);
      form.resetFields();
    };
    window.addEventListener('portal-session-cleared', onSessionCleared);
    return () => window.removeEventListener('portal-session-cleared', onSessionCleared);
  }, [form]);

  const contextValue = useMemo(() => ({ openPasswordUpdateModal }), [openPasswordUpdateModal]);
  const modalForced = forced || isForceUpdatePasswordRequired();

  return (
    <PasswordUpdateModalContext.Provider value={contextValue}>
      {children}
      <Modal
        title={modalForced ? '请修改初始密码' : '修改密码'}
        open={open}
        onCancel={closePasswordUpdateModal}
        closable={!modalForced}
        maskClosable={!modalForced}
        keyboard={!modalForced}
        destroyOnClose
        centered
        footer={[
          !modalForced && (
            <Button key="cancel" onClick={closePasswordUpdateModal}>
              取消
            </Button>
          ),
          <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit}>
            确认修改
          </Button>,
        ].filter(Boolean)}
      >
        {modalForced && (
          <p style={{ marginBottom: 16, color: '#666' }}>
            为保障账号安全，请先设置新密码后继续使用。
          </p>
        )}
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[{ required: true, message: '请输入新密码' }]}
          >
            <Input.Password placeholder="请输入新密码" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的新密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </PasswordUpdateModalContext.Provider>
  );
};

export const usePasswordUpdateModal = () => useContext(PasswordUpdateModalContext);
